import { Liquid } from 'liquidjs';

const liquid = new Liquid();

/**
 * Parse the variables an out-price calculation template references (liquidjs `fullVariablesSync`),
 * so callers can match against the parsed AST rather than substring-matching. Unparseable templates
 * reference nothing (they can't render anyway), matching the backend's conservative-but-here-safe stance.
 */
export function templateVariables(template: string): string[] {
  try {
    return liquid.fullVariablesSync(template);
  } catch {
    return [];
  }
}

function referencesKeyInNamespace(variables: string[], key: string, namespace: string): boolean {
  const nested = `${namespace}.${key}`;
  return variables.some((v) => v === key || v === nested || v.startsWith(`${nested}.`));
}

/**
 * Whether an out-price calculation template references the out-price `key` — either as a flat
 * variable (`{{ key }}`) or via the `outprices.<key>.*` namespace. Parsed from the template's
 * variables rather than substring-matched, so it is insensitive to spacing/filters and matches the
 * backend's AST-based detection.
 */
export function containsKeyReference(template: string, key: string): boolean {
  return referencesKeyInNamespace(templateVariables(template), key, 'outprices');
}

/**
 * Whether a template's parsed variables reference a custom-field `key` — flat (`{{ key }}`) or via
 * the `customfields.<key>.*` namespace. Mirrors the backend's
 * OutPriceValueCalculationService.ReferencesCustomFieldKey.
 */
export function variablesReferenceCustomFieldKey(variables: string[], key: string): boolean {
  return referencesKeyInNamespace(variables, key, 'customfields');
}

/**
 * Whether a template references the `landed_cost` variable. Parsed from the template's variables (same
 * basis as {@link containsKeyReference}) so it matches the backend's AST-based detection.
 */
export function referencesLandedCost(template: string): boolean {
  return templateVariables(template).some((v) => v === 'landed_cost' || v.startsWith('landed_cost.'));
}
